import { useEffect, useRef, useState } from 'react';
import mpegts from 'mpegts.js';

// Monta un player FLV/MSE sobre un `<video>` para una URL de streaming ya
// resuelta (FE-14). Compartido entre `LiveVideoPlayer` (video en vivo del
// vehículo) y la previsualización de la sonda de cámaras (admin) — mismo
// mecanismo, distinto origen de la URL.
//
// El cierre del proveedor NO es confiable (`close.stream` devuelve `1202` y
// el stream sigue fluyendo, ADR-018 §5.4): el cliente SIEMPRE destruye su
// propio player al desmontar o al cambiar de URL, sin esperar un corte remoto.
export function useMpegtsPlayer(url: string | null) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<ReturnType<typeof mpegts.createPlayer> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    if (!url) return undefined;

    if (!mpegts.isSupported()) {
      setError('Este navegador no soporta reproducción de video en vivo (Media Source Extensions).');
      return undefined;
    }

    const player = mpegts.createPlayer({ type: 'flv', isLive: true, url });
    playerRef.current = player;
    player.on(mpegts.Events.ERROR, (_type, detail) => {
      setError(`Error de reproducción: ${detail ?? 'desconocido'}`);
    });
    if (videoRef.current) {
      player.attachMediaElement(videoRef.current);
      player.load();
      // Autoplay con audio puede requerir un gesto del usuario en algunos
      // navegadores — el <video> queda con controles si el autoplay falla.
      Promise.resolve(player.play()).catch(() => {});
    }

    return () => {
      player.destroy();
      playerRef.current = null;
    };
  }, [url]);

  return { videoRef, error };
}
